// HTTP wrappers for the four GrataQ endpoints the SPA touches.
//
// Each non-2xx response is mapped to a typed Error. Callers catch by class
// name (instanceof) and translate to a UI state. We do this here, not at
// the call site, so the rest of the app deals in domain errors instead of
// HTTP status codes.

export class LinkNotFoundError extends Error {}
export class LinkExpiredError extends Error {}
export class LinkPausedError extends Error {}
export class NoPrekeysError extends Error {}
export class TooLargeError extends Error {}
export class UploadFailedError extends Error {}

/**
 * API base URL. Resolution order:
 *   1. Explicit window.GRATAQ_API_BASE (Playwright tests, custom embeds).
 *   2. Auto-detected localhost dev mode -> http://localhost:5243.
 *   3. Production default -> https://api.grataq.com.
 */
export function apiBase() {
  if (typeof window === 'undefined') return 'https://api.grataq.com';
  // eslint-disable-next-line no-undef
  if (window.GRATAQ_API_BASE) return window.GRATAQ_API_BASE;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
    return 'http://localhost:5243';
  }
  return 'https://api.grataq.com';
}

/** GET /api/v1/upload-links/{token}/config — public. */
export async function getConfig(token) {
  const resp = await fetch(`${apiBase()}/api/v1/upload-links/${encodeURIComponent(token)}/config`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'omit',
  });
  if (resp.status === 404) throw new LinkNotFoundError();
  if (resp.status === 410) throw new LinkExpiredError();
  if (!resp.ok) throw new Error(`config fetch failed: HTTP ${resp.status}`);
  return await resp.json();
}

/** GET /api/v1/accounts/{handle}/prekey — atomically consumes one pre-key. */
export async function getPrekey(handle) {
  const resp = await fetch(`${apiBase()}/api/v1/accounts/${encodeURIComponent(handle)}/prekey`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'omit',
  });
  if (resp.status === 404) throw new NoPrekeysError();
  // 429 = rate-limited (transient) — fall through to the generic retry message.
  if (resp.status === 429) throw new Error('prekey fetch rate-limited: HTTP 429');
  if (!resp.ok) throw new Error(`prekey fetch failed: HTTP ${resp.status}`);
  return await resp.json();
}

/**
 * Upload one encrypted blob via the 3-step flow: initiate → PUT bytes → finalize.
 * The bytes go inline to the API, to a single presigned PUT, or to multipart parts,
 * depending on what initiate returns. Progress is driven by the byte upload (PUT).
 *
 * @param handle      recipient handle from config
 * @param token       upload-link token (stays on the initiate query string)
 * @param blob        { prekeyId, kemCiphertextB64, ivB64, data: Uint8Array }
 * @param onProgress  (loaded, total) -> void; called frequently during the PUT(s)
 * @returns           { blob_id, expires_at }
 */
export async function uploadBlob(handle, token, blob, onProgress) {
  const init = await initiateUpload(handle, token, blob);

  let finalizeBody = null;
  if (init.mode === 'multipart') {
    const parts = await uploadParts(init, blob.data, onProgress);
    finalizeBody = { upload_id: init.upload_id, parts };
  } else {
    await withUploadRetry(() => putBytes(resolveUrl(init.upload_url), blob.data, onProgress));
  }

  return await finalizeUpload(init.blob_id, finalizeBody);
}

/** Absolute URL = presigned object storage; relative = the API (prefix with apiBase). */
function resolveUrl(url) {
  return /^https?:\/\//i.test(url) ? url : apiBase() + url;
}

async function initiateUpload(handle, token, blob) {
  const url = `${apiBase()}/api/v1/accounts/${encodeURIComponent(handle)}/blobs/initiate`
    + `?upload_token=${encodeURIComponent(token)}`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'omit',
    body: JSON.stringify({
      prekey_id: blob.prekeyId,
      kem_ciphertext: blob.kemCiphertextB64,
      iv: blob.ivB64,
      size: blob.data.byteLength,
    }),
  });
  if (resp.status === 410) throw new LinkExpiredError();
  if (resp.status === 409) throw new LinkPausedError();
  if (resp.status === 413) throw new TooLargeError();
  if (resp.status === 404) throw new LinkNotFoundError();
  if (!resp.ok) throw new UploadFailedError(`initiate HTTP ${resp.status}`);
  return await resp.json();
}

async function finalizeUpload(blobId, body) {
  const resp = await fetch(`${apiBase()}/api/v1/blobs/${encodeURIComponent(blobId)}/finalize`, {
    method: 'POST',
    headers: body
      ? { 'Content-Type': 'application/json', Accept: 'application/json' }
      : { Accept: 'application/json' },
    credentials: 'omit',
    body: body ? JSON.stringify(body) : undefined,
  });
  if (resp.status === 413) throw new TooLargeError();
  if (!resp.ok) throw new UploadFailedError(`finalize HTTP ${resp.status}`);
  return await resp.json();
}

// Large uploads run for minutes on slow links; a transient connection stall
// (ERR_CONNECTION_TIMED_OUT) or a 5xx shouldn't kill the whole transfer. We re-PUT
// the same body/part — presigned URLs + S3 object/part PUTs are idempotent. A 4xx
// (e.g. 413 too-large) is permanent and is NOT retried.
const UPLOAD_MAX_ATTEMPTS = 4;

const uploadDelay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Tag an error as a transient upload failure worth retrying. */
function transient(err) {
  err.retryable = true;
  return err;
}

/** Run a PUT attempt, retrying only transient failures with exponential backoff (1s/2s/4s). */
async function withUploadRetry(attempt) {
  let lastErr;
  for (let n = 1; n <= UPLOAD_MAX_ATTEMPTS; n++) {
    try {
      return await attempt();
    } catch (err) {
      lastErr = err;
      if (n === UPLOAD_MAX_ATTEMPTS || !(err && err.retryable === true)) throw err;
      await uploadDelay(1000 * 2 ** (n - 1));
    }
  }
  throw lastErr;
}

/** PUT the whole body to one URL with progress (inline / single-PUT object). */
function putBytes(url, data, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.upload.onprogress = (ev) => {
      if (onProgress && ev.lengthComputable) onProgress(ev.loaded, ev.total);
    };
    xhr.onerror = () => reject(transient(new UploadFailedError('network error')));
    xhr.onabort = () => reject(new UploadFailedError('aborted'));
    xhr.ontimeout = () => reject(transient(new UploadFailedError('timeout')));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) { resolve(xhr); return; }
      if (xhr.status === 413) { reject(new TooLargeError()); return; }
      const err = new UploadFailedError(`PUT HTTP ${xhr.status}`);
      if (xhr.status >= 500) err.retryable = true;
      reject(err);
    };
    xhr.send(data);
  });
}

/**
 * Multipart upload (S3, files over the server's threshold): PUT each part and
 * collect its ETag for the complete step. Sequential, with cumulative progress.
 */
async function uploadParts(init, data, onProgress) {
  const partSize = init.part_size_bytes;
  const parts = [...init.parts].sort((a, b) => a.part_number - b.part_number);
  const total = data.byteLength;
  const completed = [];
  let uploadedBefore = 0;

  for (const part of parts) {
    const start = (part.part_number - 1) * partSize;
    const slice = data.subarray(start, Math.min(start + partSize, total));
    const etag = await withUploadRetry(() => putPart(part.url, slice, (loaded) => {
      if (onProgress) onProgress(uploadedBefore + loaded, total);
    }));
    completed.push({ part_number: part.part_number, e_tag: etag });
    uploadedBefore += slice.byteLength;
  }
  return completed;
}

function putPart(url, data, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.upload.onprogress = (ev) => { if (onProgress && ev.lengthComputable) onProgress(ev.loaded); };
    xhr.onerror = () => reject(transient(new UploadFailedError('network error')));
    xhr.ontimeout = () => reject(transient(new UploadFailedError('timeout')));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.getResponseHeader('ETag') || '');
        return;
      }
      const err = new UploadFailedError(`PUT part HTTP ${xhr.status}`);
      if (xhr.status >= 500) err.retryable = true;
      reject(err);
    };
    xhr.send(data);
  });
}
